import {
  IResourceComponentsProps,
  useNavigation,
  useRouterContext,
} from "@pankod/refine-core";
import { authProvider } from "authProvider";
import { useLocation } from "@pankod/refine-react-router-v6";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { MdGpsFixed } from "react-icons/md";
import code from "../stores/code";
import { Dropzone } from "../../components/Dropzone";
import { PhoneCodeSelect } from "../../components/PhoneCodeSelect";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { DisplayContract } from "../../components/contract/contract";
import Joyride, { CallBackProps, STATUS, Step } from "react-joyride";
import { notification } from "@pankod/refine-antd";

// LoopingText component for animated text rotation
const LoopingText: React.FC<{ texts: string[] }> = ({ texts }) => {
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setIndex((prevIndex) => (prevIndex + 1) % texts.length);
        setIsVisible(true);
      }, 250); // Half of transition duration for smooth fade
    }, 3000);

    return () => clearInterval(intervalId);
  }, [texts.length]);

  return (
    <span
      style={{
        display: "inline-block",
        position: "relative",
        textAlign: "center",
        transition: "opacity 0.5s ease-in-out, transform 0.5s ease-in-out",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)",
        verticalAlign: "baseline",
      }}
    >
      <span style={{ fontWeight: "bold" }}>{texts[index]}</span>!
    </span>
  );
};

// Benefits list for the animation
const BENEFITS = [
  "Increased Store Visibility",
  "Higher Sales Opportunities",
  "Access to Global Shoppers",
  "Zero Marketing Cost Exposure",
  "Hands-Free Fulfilment & Logistics",
  "Trusted Business Network",
  "Collaborative Growth",
  "Verified Retail Badge",
];

// Days of the week for operational hours
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const CATEGORIES = ["Fashion", "Electronics", "Food & Drink", "Home & Garden", "Sports", "Beauty", "Books", "Toys"];

// Validation schema
const registerSchema = z.object({
  title: z.string().min(1, "Store Name is required").min(2, "Store Name must be at least 2 characters"),
  legalTitle: z.string().min(1, "Store Legal Name is required").min(2, "Store Legal Name must be at least 2 characters"),
  email: z.string().min(1, "Email is required").email("Please enter a valid email address"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  phoneCode: z.string().min(1, "Phone code is required"),
  phone: z.string().min(1, "Phone is required").regex(/^[0-9+\-\s()]+$/, "Please enter a valid phone number"),
  representativeName: z.string().min(1, "Representative Name is required").min(2, "Name must be at least 2 characters"),
  representativeEmail: z.string().min(1, "Representative Email is required").email("Please enter a valid email address"),
  representativePhone: z.string().min(1, "Representative Phone is required").regex(/^[0-9+\-\s()]+$/, "Please enter a valid phone number"),
  notes: z.string().min(1, "Store Description is required").min(10, "Store Description must be at least 10 characters"),
  avatar: z.instanceof(File, { message: "Store Logo is required" }).nullable().refine((file) => file !== null, "Store Logo is required"),
  images: z.array(z.instanceof(File)).min(1, "At least one store image is required"),
  categories: z.array(z.string()).min(1, "At least one category is required"),
  products: z.array(z.object({
    description: z.string().min(1, "Product description is required"),
    image: z.instanceof(File).nullable().refine((file) => file !== null, "Product image is required"),
  })).min(1, "At least one product is required"),
  operationalHours: z.array(z.object({
    day: z.string(),
    open: z.string(),
    close: z.string(),
    closed: z.boolean(),
  })).refine((hours) => {
    // Validate that open time is before close time for non-closed days
    for (const dayHours of hours) {
      if (!dayHours.closed) {
        // Parse time strings (HH:MM format) for comparison
        const [openHours, openMinutes] = dayHours.open.split(':').map(Number);
        const [closeHours, closeMinutes] = dayHours.close.split(':').map(Number);

        const openTime = openHours * 60 + openMinutes;
        const closeTime = closeHours * 60 + closeMinutes;

        if (openTime >= closeTime) {
          return false;
        }
      }
    }
    return true;
  }, {
    message: "Opening time must be before closing time for all open days",
  }),
  contract: z.boolean().refine((val) => val === true, "You must accept the contract"),
  address: z.object({
    country: z.string().min(1, "Country is required"),
    city: z.string().min(1, "City is required"),
    address2: z.string().min(1, "Full Address is required"),
    postal_code: z.string().min(1, "Postal Code is required"),
    state: z.string().optional(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
  }),
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const Register: React.FC<IResourceComponentsProps> = () => {
  const { Link } = useRouterContext();
  const { list } = useNavigation();
  const location = useLocation();
  const [showContract, setShowContract] = useState(false);
  const [isExpandedHours, setIsExpandedHours] = useState(false);

  // Tutorial state
  const [runTutorial, setRunTutorial] = useState(false);
  const [tutorialSteps, setTutorialSteps] = useState<Step[]>([]);

  // Test flow state - used to simulate the registration flow without actually submitting to database
  const [isTestingFlow, setIsTestingFlow] = useState(false);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    trigger,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: "onChange", // Show errors as user types
    reValidateMode: "onChange",
    defaultValues: {
      title: "",
      legalTitle: "",
      email: "",
      password: "",
      phoneCode: "+44",
      phone: "",
      representativeName: "",
      representativeEmail: "",
      representativePhone: "",
      notes: "",
      avatar: null,
      images: [],
      categories: [],
      products: [],
      operationalHours: DAYS.map(day => ({ day, open: "09:00", close: "17:00", closed: false })),
      contract: false,
      address: {
        country: "",
        city: "",
        address2: "",
        postal_code: "",
        state: "",
        lat: null,
        lng: null,
      },
    },
  });

  // Watch form values for controlled components
  const formData = watch();

  // Define tutorial steps
  useEffect(() => {
    const steps: Step[] = [
      {
        target: "body",
        content: (
          <div>
            <h3 style={{ marginBottom: "1rem", fontSize: "1.25rem", fontWeight: "600" }}>Welcome to WeCarryBags!</h3>
            <p style={{ lineHeight: "1.6" }}>Let's take a quick tour of the registration form. This will help you understand what information you need to provide to join our retail network.</p>
            <p style={{ marginTop: "0.75rem", fontSize: "0.875rem", color: "#71717a" }}>You can skip this tour at any time or restart it later.</p>
          </div>
        ),
        placement: "center",
        disableBeacon: true,
      },
      {
        target: "#tutorial-logo",
        title: "Upload Your Store Logo",
        content: "Add your store's logo here. This will be displayed on your store profile. Accepted formats: PNG, JPG, GIF up to 10MB.",
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: "#tutorial-categories",
        title: "Select Your Store Categories",
        content: "Choose the categories that best describe what your store sells. You can select multiple categories to help customers find you more easily. Just click on the pills to toggle them.",
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: "#tutorial-basic-info",
        title: "Basic Store Information",
        content: "Provide your store's essential details including name, legal name, management email, password, and phone number. This information is required for your account setup.",
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: "#tutorial-representative",
        title: "Store Representative Details",
        content: "Enter the contact information for your store's representative. This person will be the main point of contact for collaboration matters.",
        placement: "bottom",
        disableBeacon: true,
      },
      {
        target: "#tutorial-location",
        title: "Your Store Location",
        content: "Start typing your address and select from the suggestions, or click the GPS icon to use your current location. We'll automatically fill in the address details for you.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#tutorial-description",
        title: "Describe Your Store",
        content: "Write a compelling description of your store. Tell customers what makes your store unique and what they can expect when they visit. Minimum 10 characters required.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#tutorial-images",
        title: "Showcase Your Store",
        content: "Upload up to 5 high-quality images of your store. These photos will help customers get a feel for your space and products. You can drag & drop or click to upload.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#tutorial-products",
        title: "Feature Your Best Sellers",
        content: "Add 1-5 of your best-selling products with descriptions and images. This gives customers a preview of what you offer. Click 'Add Product' to add more items.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#tutorial-hours",
        title: "Set Your Operating Hours",
        content: "Define when your store is open. You can set different hours for weekdays and weekends, or expand to set individual hours for each day. Mark days as 'Closed' when you're not open.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#tutorial-contract",
        title: "Review Collaboration Contract",
        content: "You must read and accept our collaboration contract before registering. Click the button to view the full contract with your information, then accept it in the modal.",
        placement: "top",
        disableBeacon: true,
      },
      {
        target: "#tutorial-submit",
        title: "Complete Your Registration",
        content: (
          <div>
            <p style={{ marginBottom: "0.5rem" }}>Once you've filled in all required fields and accepted the contract, click here to submit your registration.</p>
            <p style={{ fontSize: "0.875rem", color: "#71717a" }}>Your account will be reviewed within 24-48 hours. You'll receive a confirmation email with your collaboration contract.</p>
          </div>
        ),
        placement: "top",
        disableBeacon: true,
      },
    ];
    setTutorialSteps(steps);
  }, []);

  // Check localStorage on mount to see if tutorial should run
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem("wecarrybags_register_tutorial_completed");
    if (!tutorialCompleted) {
      // Show tutorial after a short delay to let the page render
      setTimeout(() => {
        setRunTutorial(true);
      }, 500);
    }
  }, []);

  // Handle tutorial callback
  const handleTutorialCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRunTutorial(false);
      localStorage.setItem("wecarrybags_register_tutorial_completed", "true");
    }
  };

  // Function to manually restart tutorial
  const startTutorial = () => {
    setRunTutorial(true);
  };

  // Google Maps Places Autocomplete
  const [addressQuery, setAddressQuery] = useState("");
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationRequestInProgress, setLocationRequestInProgress] = useState(false);

  const handleAddressSelect = async (address: string) => {
    try {
      const results = await geocodeByAddress(address);
      const selectedAddress = results[0];
      const addressComponents = selectedAddress.address_components;
      const { lat, lng } = await getLatLng(selectedAddress);

      // Extract address components
      const extractedCity = addressComponents.find((component: any) =>
        component.types.includes("postal_town")
      ) || addressComponents.find((component: any) =>
        component.types.includes("locality")
      );
      const extractedCountry = addressComponents.find((component: any) =>
        component.types.includes("country")
      );
      const extractedPostalCode = addressComponents.find((component: any) =>
        component.types.includes("postal_code")
      );
      const extractedState = addressComponents.find((component: any) =>
        component.types.includes("administrative_area_level_1")
      );

      const addressData = {
        address2: addressComponents[0]?.long_name + (addressComponents[1] ? ", " + addressComponents[1].long_name : "") || "",
        city: extractedCity?.long_name || "",
        state: extractedState?.long_name || "",
        country: extractedCountry?.long_name || "",
        postal_code: extractedPostalCode?.long_name || "",
        lat: lat,
        lng: lng,
      };

      setValue("address", addressData);
      trigger("address");
      setAddressQuery(address);
    } catch (error) {
      console.error("Error geocoding address:", error);
    }
  };

  const handleGetCurrentLocation = () => {
    // Prevent multiple simultaneous requests
    if (locationRequestInProgress || isGettingLocation) {
      return;
    }

    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    setIsGettingLocation(true);
    setLocationRequestInProgress(true);

    // Simple, reliable geolocation options
    const options = {
      enableHighAccuracy: false,
      timeout: 20000,
      maximumAge: 300000, // 5 minutes - use cached if available
    };

    const successCallback = async (position: GeolocationPosition) => {
      try {
        const { latitude, longitude } = position.coords;

        // Reverse geocode using Google Maps Geocoding API
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=AIzaSyD9__ZEx7NbpWOPAw9KO-y9WtowN40DaCg`
        );

        if (!response.ok) {
          throw new Error("Geocoding API request failed");
        }

        const data = await response.json();

        if (data.status === "OK" && data.results && data.results.length > 0) {
          const result = data.results[0];
          const addressComponents = result.address_components;

          // Extract address components
          const extractedCity = addressComponents.find((component: any) =>
            component.types.includes("postal_town")
          ) || addressComponents.find((component: any) =>
            component.types.includes("locality")
          );
          const extractedCountry = addressComponents.find((component: any) =>
            component.types.includes("country")
          );
          const extractedPostalCode = addressComponents.find((component: any) =>
            component.types.includes("postal_code")
          );
          const extractedState = addressComponents.find((component: any) =>
            component.types.includes("administrative_area_level_1")
          );

          // Format the full address
          const formattedAddress = result.formatted_address;

          const addressData = {
            address2: addressComponents[0]?.long_name + (addressComponents[1] ? ", " + addressComponents[1].long_name : "") || "",
            city: extractedCity?.long_name || "",
            state: extractedState?.long_name || "",
            country: extractedCountry?.long_name || "",
            postal_code: extractedPostalCode?.long_name || "",
            lat: latitude,
            lng: longitude,
          };

          setValue("address", addressData);
          trigger("address");
          setAddressQuery(formattedAddress);
        } else {
          throw new Error("No address results found");
        }
      } catch (error) {
        console.error("Error processing location:", error);
        alert("Failed to get address from your location. Please try entering your address manually.");
      } finally {
        setIsGettingLocation(false);
        setLocationRequestInProgress(false);
      }
    };

    const errorCallback = (error: GeolocationPositionError) => {
      setIsGettingLocation(false);
      setLocationRequestInProgress(false);
      
      // Suppress console errors for kCLErrorLocationUnknown (macOS CoreLocation issue)
      // This is a system-level issue, not a code problem
      if (error.code === 2 && error.message?.includes("Position update is unavailable")) {
        // This is likely the macOS CoreLocation issue
        // Don't spam the user with alerts, just silently fail
        console.log("Location unavailable (this may be a macOS system issue). Please enter address manually.");
        return;
      }
      
      // Only show error after a delay to allow permission prompt to appear
      setTimeout(() => {
        if (error.code === 1) {
          // PERMISSION_DENIED
          alert("Location access was denied. You can still enter your address manually in the field above.");
        } else if (error.code === 2) {
          // POSITION_UNAVAILABLE - but only if not already handled above
          alert("Unable to determine your location. This may be due to macOS Location Services settings. Please check System Preferences > Security & Privacy > Location Services, or enter your address manually.");
        } else if (error.code === 3) {
          // TIMEOUT
          alert("Location request took too long. Please try again or enter your address manually.");
        }
      }, 500);
    };

    // Call geolocation API
    navigator.geolocation.getCurrentPosition(
      successCallback,
      errorCallback,
      options
    );
  };

  const handleAddressChange = (field: keyof RegisterFormData["address"], value: string) => {
    setValue(`address.${field}`, value as any);
    trigger(`address.${field}`);
  };

  const handleCategoryToggle = (category: string) => {
    const currentCategories = formData.categories || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter((c: string) => c !== category)
      : [...currentCategories, category];
    setValue("categories", newCategories);
    trigger("categories");
  };

  const addProduct = () => {
    const currentProducts = formData.products || [];
    if (currentProducts.length < 5) {
      setValue("products", [...currentProducts, { description: "", image: null }]);
    }
  };

  const removeProduct = (index: number) => {
    const currentProducts = formData.products || [];
    setValue("products", currentProducts.filter((_: any, i: number) => i !== index));
    trigger("products");
  };

  const updateProduct = (index: number, field: "description" | "image", value: any) => {
    const currentProducts = formData.products || [];
    const updatedProducts = currentProducts.map((p: any, i: number) =>
      i === index ? { ...p, [field]: value } : p
    );
    setValue("products", updatedProducts);
    trigger(`products.${index}.${field}`);
  };

  const updateOperationalHours = (index: number, field: string, value: any) => {
    const currentHours = formData.operationalHours || [];
    const updatedHours = currentHours.map((h: any, i: number) =>
      i === index ? { ...h, [field]: value } : h
    );
    setValue("operationalHours", updatedHours);
  };

  const updateWeekdayHours = (field: string, value: any) => {
    const currentHours = formData.operationalHours || [];
    // Update Monday through Friday (indices 0-4)
    const updatedHours = currentHours.map((h: any, i: number) => {
      if (i >= 0 && i <= 4) {
        return { ...h, [field]: value };
      }
      return h;
    });
    setValue("operationalHours", updatedHours);
  };

  // Helper function to open time picker when clicking/tapping anywhere on the input
  const handleTimeInputClick = (e: React.MouseEvent<HTMLInputElement> | React.TouchEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    if (input.disabled) return;
    
    // Try to use showPicker() if available (modern browsers)
    if ('showPicker' in HTMLInputElement.prototype && input.type === 'time') {
      try {
        (input as any).showPicker();
      } catch (err) {
        // Fallback to focus if showPicker fails
        input.focus();
        input.click();
      }
    } else {
      // Fallback for older browsers
      input.focus();
      input.click();
    }
  };

  // Validate required fields for contract before opening modal
  const validateContractFields = async () => {
    const requiredFields: Array<keyof RegisterFormData | string> = [
      "title",
      "legalTitle",
      "address.address2",
      "address.city",
      "address.country",
      "address.postal_code",
      "representativeName",
    ];

    // Trigger validation for all required fields
    const validations = await Promise.all(
      requiredFields.map(field => trigger(field as any))
    );

    // Check if all validations passed
    return validations.every(isValid => isValid);
  };

  const handleContractClick = async () => {
    const isValid = await validateContractFields();

    if (!isValid) {
      // Show error notification listing missing fields
      const missingFields = [];
      if (!formData.title) missingFields.push("Store Name");
      if (!formData.legalTitle) missingFields.push("Store Legal Name");
      if (!formData.address?.address2) missingFields.push("Full Address");
      if (!formData.address?.city) missingFields.push("City");
      if (!formData.address?.country) missingFields.push("Country");
      if (!formData.address?.postal_code) missingFields.push("Postal Code");
      if (!formData.representativeName) missingFields.push("Representative Name");

      alert(`Please fill in the following fields before viewing the contract:\n\n• ${missingFields.join("\n• ")}`);
      return;
    }

    setShowContract(true);
  };

  // Test function to simulate the registration flow without submitting to database
  // This allows reviewing loading states, redirects, and toast notifications
  const testRegistrationFlow = async () => {
    setIsTestingFlow(true);

    try {
      // Step 1: Show initial processing notification (centered)
      notification.info({
        message: "Processing Registration",
        description: "Please wait while we upload your files and process your data. This may take a moment...",
        duration: 0, // Don't auto-close
        key: "registration-progress",
        placement: "top",
        style: {
          marginTop: "40vh",
        },
        className: "centered-notification",
      });

      // Simulate some processing time
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 2: Show file upload notification (centered)
      notification.info({
        message: "Uploading Files",
        description: "Uploading store logo, images, and product photos...",
        duration: 0,
        key: "registration-progress",
        placement: "top",
        style: {
          marginTop: "40vh",
        },
        className: "centered-notification",
      });

      // Simulate file upload time
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Step 3: Close progress notification
      notification.destroy("registration-progress");

      // Step 4: Show success notification (top right)
      notification.success({
        message: "Account Created Successfully!",
        description: React.createElement(
          "div",
          null,
          React.createElement("div", { style: { marginBottom: "0.5rem" } }, "Your account has been created and a collaboration contract has been sent to your email. Your account is pending approval."),
          React.createElement(
            "div",
            { style: { marginTop: "0.75rem", padding: "0.75rem", backgroundColor: "#f9fafb", borderRadius: "0.375rem", fontSize: "0.875rem" } },
            React.createElement(
              "div",
              { style: { fontWeight: "600", marginBottom: "0.5rem", color: "#18181b" } },
              "Next Steps:"
            ),
            React.createElement("div", { style: { color: "#52525b" } }, "1. Check your email for the collaboration contract"),
            React.createElement("div", { style: { color: "#52525b" } }, "2. Wait for account approval (usually within 24-48 hours)"),
            React.createElement("div", { style: { color: "#52525b" } }, "3. Once approved, you'll receive a confirmation email")
          )
        ),
        duration: 10,
      });

      // Step 5: Simulate redirect (in test mode, we show a notification instead of actually redirecting)
      await new Promise(resolve => setTimeout(resolve, 1000));

      notification.info({
        message: "Test Mode",
        description: "In production, you would now be redirected to the orders page. No data was submitted to the database.",
        duration: 5,
      });

    } catch (error) {
      console.error("Test flow error:", error);
      notification.error({
        message: "Test Error",
        description: "An error occurred during the test flow",
        duration: 5,
      });
    } finally {
      setIsTestingFlow(false);
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    try {
      await (authProvider as any).register(data);

      const searchParams = new URLSearchParams(location.search);
      const redirect = searchParams.get("redirect");

      if (typeof redirect === "string") {
        window.location.href = redirect;
      } else {
        list("orders");
      }
    } catch (error) {
      console.error("Registration error:", error);
    }
  };

  // Modern ShadCN-inspired styles
  const getInputStyle = (hasError: boolean) => ({
    display: "block",
    width: "100%",
    padding: "0.625rem 0.75rem",
    fontSize: "0.875rem",
    fontWeight: "400",
    lineHeight: "1.5",
    color: "#09090b",
    backgroundColor: "#ffffff",
    border: hasError ? "2px solid #ef4444" : "1px solid #e4e4e7",
    borderRadius: "0.5rem",
    outline: "none",
    transition: "all 0.15s ease",
  });

  const inputStyle = getInputStyle(false);
  
  const inputFocusStyle = {
    ...inputStyle,
    borderColor: "#18181b",
    boxShadow: "0 0 0 3px rgba(24, 24, 27, 0.1)",
  };

  const inputErrorStyle = {
    ...inputStyle,
    border: "2px solid #ef4444",
  };

  const getLabelStyle = (hasError: boolean) => ({
    display: "block",
    marginBottom: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: "500",
    color: hasError ? "#ef4444" : "#09090b",
    letterSpacing: "-0.01em",
    transition: "color 0.2s",
  });

  const labelStyle = getLabelStyle(false);

  const errorStyle = {
    fontSize: "0.8125rem",
    color: "#ef4444",
    marginTop: "0.375rem",
    marginBottom: "0",
    fontWeight: "600",
    display: "block",
    lineHeight: "1.4",
    minHeight: "1.25rem",
  };

  const cardStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #e4e4e7",
    borderRadius: "0.75rem",
    padding: "1.25rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
  };

  return (
    <>
      <Helmet>
        <title>Register Your Store - WeCarryBags Partner Portal</title>
        <meta name="description" content="Join WeCarryBags as a partner store. Register your retail store to access our bag delivery network, manage orders, and grow your business. Free registration for qualified stores." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://store.wecarrybags.co.uk/register" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://store.wecarrybags.co.uk/register" />
        <meta property="og:title" content="Register Your Store - WeCarryBags Partner Portal" />
        <meta property="og:description" content="Join WeCarryBags as a partner store. Register your retail store to access our bag delivery network, manage orders, and grow your business." />
        <meta property="og:image" content="https://store.wecarrybags.co.uk/images/og-image.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://store.wecarrybags.co.uk/register" />
        <meta property="twitter:title" content="Register Your Store - WeCarryBags Partner Portal" />
        <meta property="twitter:description" content="Join WeCarryBags as a partner store. Register your retail store to access our bag delivery network, manage orders, and grow your business." />
        <meta property="twitter:image" content="https://store.wecarrybags.co.uk/images/og-image.png" />
      </Helmet>
      {/* Background with blue gradients on sides */}
      <div style={{ position: "relative", backgroundColor: "#fafafa", width: "100%" }}>
        {/* Left side gradient */}
        <img
          src="/images/gradients/blue-blob-side.png"
          alt=""
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100%",
            width: "20%",
            zIndex: 0,
            opacity: 0.6,
          }}
        />

        {/* Right side gradient */}
        <img
          src="/images/gradients/blue-blob-side.png"
          alt=""
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100%",
            width: "20%",
            transform: "rotate(180deg)",
            zIndex: 0,
            opacity: 0.6,
          }}
        />

        {/* Main container */}
        <div
          style={{
            position: "relative",
            zIndex: 1,
            minHeight: "100vh",
            paddingTop: "2rem",
            paddingBottom: "2rem",
            width: "100%",
          }}
        >
          <div
            className="register-form-container"
            style={{
              maxWidth: "68rem",
              margin: "0 auto",
              paddingLeft: "1.5rem",
              paddingRight: "1.5rem",
            }}
          >
            {/* Logo */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "2rem",
              }}
            >
              <Link to="/">
                <img
                  src="/images/logo.png?v=2"
                  alt="Logo"
                  style={{ objectFit: "contain", width: "5rem" }}
                />
              </Link>
            </div>

            {/* Form container */}
            <div
              className="register-form"
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "1rem",
                padding: "3rem",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.06)",
                border: "1px solid #e4e4e7",
              }}
            >
              <div style={{ position: "relative" }}>
                <h1
                  style={{
                    textAlign: "center",
                    marginBottom: "0.5rem",
                    fontSize: "2.5rem",
                    fontWeight: "700",
                    background: "linear-gradient(90deg, #5289FF 32.21%, #FFA600 57.21%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                    color: "transparent",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Create Your Account
                </h1>
                <button
                  type="button"
                  onClick={startTutorial}
                  className="take-tour-button"
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "0",
                    transform: "translateY(-50%)",
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#5289FF",
                    backgroundColor: "transparent",
                    border: "1px solid #5289FF",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.375rem",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#5289FF";
                    e.currentTarget.style.color = "#ffffff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#5289FF";
                  }}
                >
                  <span>Take Tour</span>
                </button>
              </div>
              <p
                className="store-details-intro"
                style={{
                  textAlign: "center",
                  marginBottom: "2.5rem",
                  fontSize: "1.125rem",
                  color: "#71717a",
                  lineHeight: "1.6",
                }}
              >
                Fill in your store details to get started benefitting from joining the WeCarryBags Retail Network with{" "}
                <span className="looping-text-container">
                  <LoopingText texts={BENEFITS} />
                </span>
              </p>
              {/* Mobile Take Tour Button - shown below sub-heading */}
              <div className="take-tour-button-mobile" style={{ display: "none" }}>
                <button
                  type="button"
                  onClick={startTutorial}
                  style={{
                    padding: "0.5rem 1rem",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#5289FF",
                    backgroundColor: "transparent",
                    border: "1px solid #5289FF",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    margin: "0 auto",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#5289FF";
                    e.currentTarget.style.color = "#ffffff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#5289FF";
                  }}
                >
                  <span>Take Tour</span>
                </button>
              </div>
              <style>{`
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }

                /* Centered notifications for registration flow */
                .centered-notification {
                  left: 50% !important;
                  transform: translateX(-50%) !important;
                  max-width: 500px !important;
                }

                .centered-notification .ant-notification-notice {
                  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
                }

                @media (max-width: 768px) {
                  .looping-text-container {
                    display: inline-flex;
                    align-items: center;
                    height: 50px;
                    vertical-align: middle;
                    position: relative;
                  }
                  .take-tour-button {
                    display: none !important;
                  }
                  .store-details-intro {
                    margin-bottom: 1rem !important;
                  }
                  .take-tour-button-mobile {
                    display: block !important;
                    margin-bottom: 1.5rem;
                  }
                }
                @media (min-width: 481px) and (max-width: 768px) {
                  .store-details-intro {
                    height: 130px;
                    margin-bottom: 0.5rem;
                  }
                }
                @media (min-width: 769px) {
                  .operational-hours-container {
                    grid-template-columns: repeat(3, 1fr) !important;
                  }
                }
                @media (max-width: 480px) {
                  .register-form {
                    width: calc(100% + 1.5rem) !important;
                    margin-left: -0.75rem !important;
                    margin-right: -0.75rem !important;
                    padding-left: 1.5rem !important;
                    padding-right: 1.5rem !important;
                    padding-top: 3rem !important;
                    padding-bottom: 3rem !important;
                    border-radius: 0.5rem !important;
                  }
                  .register-form-loading-overlay {
                    border-radius: 0.5rem !important;
                  }
                }
              `}</style>

              <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ position: "relative" }}>
                {/* Loading overlay for form content - disabled state without spinner */}
                {(isSubmitting || isTestingFlow) && (
                  <div
                    className="register-form-loading-overlay"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.6)",
                      borderRadius: "0.5rem",
                      zIndex: 10,
                      cursor: "not-allowed",
                    }}
                  />
                )}
                {/* Store Logo Upload */}
                <div id="tutorial-logo" style={{ marginBottom: "2rem" }}>
                  <label style={getLabelStyle(!!errors.avatar)}>
                    Store Logo <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Controller
                    name="avatar"
                    control={control}
                    render={({ field }) => (
                      <Dropzone
                        onDrop={(files) => {
                          field.onChange(files[0] || null);
                          trigger("avatar");
                        }}
                        maxFiles={1}
                        maxSize={10 * 1024 * 1024}
                        value={field.value || undefined}
                        description="PNG, JPG, GIF up to 10MB"
                      />
                    )}
                  />
                  {errors.avatar && (
                    <p style={errorStyle} role="alert">
                      {errors.avatar.message as string}
                    </p>
                  )}
                </div>

                {/* Categories */}
                <div id="tutorial-categories" style={{ marginBottom: "2rem" }}>
                  <label style={getLabelStyle(!!errors.categories)}>
                    Categories <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <div style={cardStyle}>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.75rem",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {CATEGORIES.map((category) => {
                        const isSelected = (formData.categories || []).includes(category);
                        return (
                          <button
                            key={category}
                            type="button"
                            onClick={() => handleCategoryToggle(category)}
                            style={{
                              padding: "0.625rem 1.25rem",
                              backgroundColor: isSelected ? "#5289FF" : "transparent",
                              color: isSelected ? "#ffffff" : "#52525b",
                              border: isSelected ? "1px solid #5289FF" : errors.categories ? "1px solid #ef4444" : "1px solid #d1d5db",
                              borderRadius: "9999px",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              fontWeight: "600",
                              transition: "all 0.2s ease",
                              outline: "none",
                              userSelect: "none",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = "#f9fafb";
                                e.currentTarget.style.borderColor = "#9ca3af";
                                e.currentTarget.style.color = "#09090b";
                              } else {
                                e.currentTarget.style.backgroundColor = "#3d7ae6";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.borderColor = errors.categories ? "#ef4444" : "#d1d5db";
                                e.currentTarget.style.color = "#52525b";
                              } else {
                                e.currentTarget.style.backgroundColor = "#5289FF";
                              }
                            }}
                          >
                            {category}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {errors.categories && (
                    <p style={errorStyle} role="alert">
                      {errors.categories.message as string}
                    </p>
                  )}
                </div>

                {/* Basic Information Grid */}
                <div
                  id="tutorial-basic-info"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                    gap: "1.5rem",
                    marginBottom: "2rem",
                  }}
                >
                  <div>
                    <label style={getLabelStyle(!!errors.title)}>
                      Store Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      {...register("title")}
                      type="text"
                      placeholder="Enter store name"
                      style={getInputStyle(!!errors.title)}
                      onFocus={(e) => Object.assign(e.target.style, errors.title ? inputErrorStyle : inputFocusStyle)}
                      onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.title))}
                      className="placeholder-slate-400"
                      aria-invalid={errors.title ? "true" : "false"}
                    />
                    {errors.title && (
                      <p style={errorStyle} role="alert">
                        {errors.title.message as string}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={getLabelStyle(!!errors.legalTitle)}>
                      Store Legal Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      {...register("legalTitle")}
                      type="text"
                      placeholder="Enter legal name"
                      style={getInputStyle(!!errors.legalTitle)}
                      onFocus={(e) => Object.assign(e.target.style, errors.legalTitle ? inputErrorStyle : inputFocusStyle)}
                      onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.legalTitle))}
                      className="placeholder-slate-400"
                      aria-invalid={errors.legalTitle ? "true" : "false"}
                    />
                    {errors.legalTitle && (
                      <p style={errorStyle} role="alert">
                        {errors.legalTitle.message as string}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={getLabelStyle(!!errors.email)}>
                      Management Email <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      {...register("email")}
                      type="email"
                      placeholder="email@example.com"
                      style={getInputStyle(!!errors.email)}
                      onFocus={(e) => Object.assign(e.target.style, errors.email ? inputErrorStyle : inputFocusStyle)}
                      onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.email))}
                      className="placeholder-slate-400"
                      aria-invalid={errors.email ? "true" : "false"}
                    />
                    {errors.email && (
                      <p style={errorStyle} role="alert">
                        {errors.email.message as string}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={getLabelStyle(!!errors.password)}>
                      Password <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      {...register("password")}
                      type="password"
                      placeholder="Enter password"
                      style={getInputStyle(!!errors.password)}
                      onFocus={(e) => Object.assign(e.target.style, errors.password ? inputErrorStyle : inputFocusStyle)}
                      onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.password))}
                      className="placeholder-slate-400"
                      aria-invalid={errors.password ? "true" : "false"}
                    />
                    {errors.password && (
                      <p style={errorStyle} role="alert">
                        {errors.password.message as string}
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={getLabelStyle(!!errors.phone)}>
                      Management Phone <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <Controller
                        name="phoneCode"
                        control={control}
                        render={({ field }) => (
                          <PhoneCodeSelect
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              trigger("phoneCode");
                            }}
                            style={{ width: "6.5rem", flexShrink: 0 }}
                          />
                        )}
                      />
                      <input
                        {...register("phone")}
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9+\s()\-]*"
                        placeholder="Phone number"
                        style={{ ...getInputStyle(!!errors.phone), flex: 1 }}
                        onFocus={(e) => Object.assign(e.target.style, errors.phone ? inputErrorStyle : inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.phone))}
                        onKeyPress={(e) => {
                          // Only allow numbers, +, -, spaces, and parentheses
                          if (!/[0-9+\s()\-]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className="placeholder-slate-400"
                        aria-invalid={errors.phone ? "true" : "false"}
                      />
                    </div>
                    {errors.phone && (
                      <p style={errorStyle} role="alert">
                        {errors.phone.message as string}
                      </p>
                    )}
                  </div>
                </div>

                {/* Representative Information Section */}
                <div id="tutorial-representative" style={{ marginBottom: "2rem" }}>
                  <h3
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      marginBottom: "1.5rem",
                      color: "#09090b",
                      paddingBottom: "0.75rem",
                      borderBottom: "2px solid #f4f4f5",
                    }}
                  >
                    Store Representative
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                      gap: "1.5rem",
                    }}
                  >
                    <div>
                      <label style={getLabelStyle(!!errors.representativeName)}>
                        Name <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        {...register("representativeName")}
                        type="text"
                        placeholder="Enter representative name"
                        style={getInputStyle(!!errors.representativeName)}
                        onFocus={(e) => Object.assign(e.target.style, errors.representativeName ? inputErrorStyle : inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.representativeName))}
                        className="placeholder-slate-400"
                        aria-invalid={errors.representativeName ? "true" : "false"}
                      />
                      {errors.representativeName && (
                        <p style={errorStyle} role="alert">
                          {errors.representativeName.message as string}
                        </p>
                      )}
                    </div>

                    <div>
                      <label style={getLabelStyle(!!errors.representativeEmail)}>
                        Email <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        {...register("representativeEmail")}
                        type="email"
                        placeholder="representative@example.com"
                        style={getInputStyle(!!errors.representativeEmail)}
                        onFocus={(e) => Object.assign(e.target.style, errors.representativeEmail ? inputErrorStyle : inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.representativeEmail))}
                        className="placeholder-slate-400"
                        aria-invalid={errors.representativeEmail ? "true" : "false"}
                      />
                      {errors.representativeEmail && (
                        <p style={errorStyle} role="alert">
                          {errors.representativeEmail.message as string}
                        </p>
                      )}
                    </div>

                    <div>
                      <label style={getLabelStyle(!!errors.representativePhone)}>
                        Phone <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <Controller
                          name="phoneCode"
                          control={control}
                          render={({ field }) => (
                            <PhoneCodeSelect
                              value={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                trigger("phoneCode");
                              }}
                              style={{ width: "6.5rem", flexShrink: 0 }}
                            />
                          )}
                        />
                        <input
                          {...register("representativePhone")}
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9+\s()\-]*"
                          placeholder="Phone number"
                          style={{ ...getInputStyle(!!errors.representativePhone), flex: 1 }}
                          onFocus={(e) => Object.assign(e.target.style, errors.representativePhone ? inputErrorStyle : inputFocusStyle)}
                          onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.representativePhone))}
                          onKeyPress={(e) => {
                            // Only allow numbers, +, -, spaces, and parentheses
                            if (!/[0-9+\s()\-]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          className="placeholder-slate-400"
                          aria-invalid={errors.representativePhone ? "true" : "false"}
                        />
                      </div>
                      {errors.representativePhone && (
                        <p style={errorStyle} role="alert">
                          {errors.representativePhone.message as string}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Store Location with Google Maps Places Autocomplete */}
                <div id="tutorial-location" style={{ marginBottom: "2rem" }}>
                  <h3
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      marginBottom: "1.5rem",
                      color: "#09090b",
                      paddingBottom: "0.75rem",
                      borderBottom: "2px solid #f4f4f5",
                    }}
                  >
                    Store Location
                  </h3>
                  <div style={cardStyle}>
                    {/* Google Maps Places Autocomplete */}
                    <div style={{ marginBottom: "1rem", position: "relative" }}>
                      <label style={labelStyle}>Search Address</label>
                      <PlacesAutocomplete
                        value={addressQuery}
                        onChange={setAddressQuery}
                        onSelect={handleAddressSelect}
                      >
                        {({
                          getInputProps,
                          suggestions,
                          getSuggestionItemProps,
                          loading,
                        }) => (
                          <div style={{ position: "relative" }}>
                            <input
                              {...getInputProps({
                                placeholder: "Start typing your address...",
                                style: {
                                  ...inputStyle,
                                  paddingRight: "2.5rem",
                                },
                                className: "placeholder-slate-400",
                                onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                                  Object.assign(e.target.style, {
                                    ...inputFocusStyle,
                                    paddingRight: "2.5rem",
                                  });
                                },
                                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                  Object.assign(e.target.style, {
                                    ...inputStyle,
                                    paddingRight: "2.5rem",
                                  });
                                },
                              })}
                            />
                            <button
                              type="button"
                              onClick={handleGetCurrentLocation}
                              disabled={isGettingLocation}
                              style={{
                                position: "absolute",
                                right: "0.75rem",
                                top: "50%",
                                transform: "translateY(-50%)",
                                background: "none",
                                border: "none",
                                cursor: isGettingLocation ? "not-allowed" : "pointer",
                                padding: "0.25rem",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: isGettingLocation ? "#a1a1aa" : "#71717a",
                                transition: "color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                if (!isGettingLocation) {
                                  e.currentTarget.style.color = "#09090b";
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isGettingLocation) {
                                  e.currentTarget.style.color = "#71717a";
                                }
                              }}
                              title="Use current location"
                            >
                              <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {/* @ts-ignore */}
                                <MdGpsFixed
                                  size={20}
                                  style={{
                                    animation: isGettingLocation ? "spin 1s linear infinite" : "none",
                                  }}
                                />
                              </span>
                            </button>
                            <style>{`
                              @keyframes spin {
                                from { transform: rotate(0deg); }
                                to { transform: rotate(360deg); }
                              }
                            `}</style>
                            {loading && (
                              <div style={{
                                position: "absolute",
                                left: "0.75rem",
                                top: "2.5rem",
                                fontSize: "0.875rem",
                                color: "#71717a"
                              }}>
                                Loading...
                              </div>
                            )}
                            {suggestions.length > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  backgroundColor: "#ffffff",
                                  border: "1px solid #e4e4e7",
                                  borderRadius: "0.5rem",
                                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                  margin: "0.25rem 0 0 0",
                                  zIndex: 50,
                                  maxHeight: "200px",
                                  overflowY: "auto",
                                }}
                              >
                                {suggestions.map((suggestion: any) => {
                                  const style = {
                                    padding: "0.625rem",
                                    cursor: "pointer",
                                    borderRadius: "0.375rem",
                                    fontSize: "0.875rem",
                                    transition: "background-color 0.15s",
                                    backgroundColor: suggestion.active ? "#f4f4f5" : "transparent",
                                  };
                                  return (
                                    <div
                                      {...getSuggestionItemProps(suggestion, { style })}
                                      key={suggestion.placeId}
                                      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                                        e.currentTarget.style.backgroundColor = "#f4f4f5";
                                      }}
                                      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                      }}
                                    >
                                      <div style={{ fontWeight: "500", color: "#09090b" }}>
                                        {suggestion.formattedSuggestion?.mainText || suggestion.description.split(',')[0]}
                                      </div>
                                      <div style={{ fontSize: "0.75rem", color: "#71717a" }}>
                                        {suggestion.formattedSuggestion?.secondaryText || suggestion.description.split(',').slice(1).join(',')}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </PlacesAutocomplete>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                        gap: "1rem",
                      }}
                    >
                      <div>
                        <label style={getLabelStyle(!!errors.address?.address2)}>
                          Full Address <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          {...register("address.address2")}
                          type="text"
                          placeholder="Enter full address"
                          style={getInputStyle(!!errors.address?.address2)}
                          onFocus={(e) => Object.assign(e.target.style, errors.address?.address2 ? inputErrorStyle : inputFocusStyle)}
                          onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.address?.address2))}
                          className="placeholder-slate-400"
                          aria-invalid={errors.address?.address2 ? "true" : "false"}
                        />
                        {errors.address?.address2 && (
                          <p style={errorStyle} role="alert">
                            {errors.address.address2.message as string}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={getLabelStyle(!!errors.address?.city)}>
                          City <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          {...register("address.city")}
                          type="text"
                          placeholder="Enter city"
                          style={getInputStyle(!!errors.address?.city)}
                          onFocus={(e) => Object.assign(e.target.style, errors.address?.city ? inputErrorStyle : inputFocusStyle)}
                          onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.address?.city))}
                          className="placeholder-slate-400"
                          aria-invalid={errors.address?.city ? "true" : "false"}
                        />
                        {errors.address?.city && (
                          <p style={errorStyle} role="alert">
                            {errors.address.city.message as string}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={labelStyle}>State/Province</label>
                        <input
                          {...register("address.state")}
                          type="text"
                          placeholder="Enter state"
                          style={getInputStyle(false)}
                          onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                          onBlur={(e) => Object.assign(e.target.style, getInputStyle(false))}
                          className="placeholder-slate-400"
                        />
                      </div>

                      <div>
                        <label style={getLabelStyle(!!errors.address?.country)}>
                          Country <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          {...register("address.country")}
                          type="text"
                          placeholder="Enter country"
                          style={getInputStyle(!!errors.address?.country)}
                          onFocus={(e) => Object.assign(e.target.style, errors.address?.country ? inputErrorStyle : inputFocusStyle)}
                          onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.address?.country))}
                          className="placeholder-slate-400"
                          aria-invalid={errors.address?.country ? "true" : "false"}
                        />
                        {errors.address?.country && (
                          <p style={errorStyle} role="alert">
                            {errors.address.country.message as string}
                          </p>
                        )}
                      </div>

                      <div>
                        <label style={getLabelStyle(!!errors.address?.postal_code)}>
                          Postal Code <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          {...register("address.postal_code")}
                          type="text"
                          placeholder="Enter postal code"
                          style={getInputStyle(!!errors.address?.postal_code)}
                          onFocus={(e) => Object.assign(e.target.style, errors.address?.postal_code ? inputErrorStyle : inputFocusStyle)}
                          onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.address?.postal_code))}
                          className="placeholder-slate-400"
                          aria-invalid={errors.address?.postal_code ? "true" : "false"}
                        />
                        {errors.address?.postal_code && (
                          <p style={errorStyle} role="alert">
                            {errors.address.postal_code.message as string}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Store Description */}
                <div id="tutorial-description" style={{ marginBottom: "2rem" }}>
                  <label style={getLabelStyle(!!errors.notes)}>
                    Store Description <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <textarea
                    {...register("notes")}
                    placeholder="Tell us about your store..."
                    rows={4}
                    style={{ ...getInputStyle(!!errors.notes), resize: "vertical", minHeight: "100px" }}
                    onFocus={(e) => Object.assign(e.target.style, errors.notes ? inputErrorStyle : inputFocusStyle)}
                    onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.notes))}
                    className="placeholder-slate-400"
                    aria-invalid={errors.notes ? "true" : "false"}
                  />
                  {errors.notes && (
                    <p style={errorStyle} role="alert">
                      {errors.notes.message as string}
                    </p>
                  )}
                </div>

                {/* Store Images */}
                <div id="tutorial-images" style={{ marginBottom: "2rem" }}>
                  <label style={getLabelStyle(!!errors.images)}>
                    Store Images (up to 5) <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <Controller
                    name="images"
                    control={control}
                    render={({ field }) => (
                      <Dropzone
                        onDrop={(files) => {
                          const existingFiles = field.value || [];
                          const currentCount = existingFiles.length;
                          const maxCount = 5;
                          
                          // If incoming files array is smaller than existing, it's a deletion - replace
                          if (files.length < currentCount) {
                            field.onChange(files);
                          } else if (currentCount >= maxCount) {
                            // Replace all files if at limit
                            field.onChange(files.slice(0, maxCount));
                          } else {
                            // Merge with existing files, up to max of 5
                            const remainingSlots = maxCount - currentCount;
                            const filesToAdd = files.slice(0, remainingSlots);
                            field.onChange([...existingFiles, ...filesToAdd]);
                          }
                          trigger("images");
                        }}
                        maxFiles={5}
                        maxSize={10 * 1024 * 1024}
                        value={field.value}
                        description={
                          (field.value?.length || 0) >= 5
                            ? "Maximum 5 images reached. Drop new files to replace."
                            : "Upload up to 5 images (PNG, JPG, GIF up to 10MB each)"
                        }
                      />
                    )}
                  />
                  {errors.images && (
                    <p style={errorStyle} role="alert">
                      {errors.images.message as string}
                    </p>
                  )}
                </div>

                {/* Best Seller Items */}
                <div id="tutorial-products" style={{ marginBottom: "2rem" }}>
                  <label style={getLabelStyle(!!errors.products)}>
                    Best Seller Items <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  {(formData.products || []).map((product: any, index: number) => (
                    <div key={index} style={{ ...cardStyle, marginBottom: "1rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                        <span style={{ fontWeight: "600", fontSize: "0.875rem" }}>Product {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          style={{
                            color: "#ef4444",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            padding: "0.25rem 0.5rem",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                      <textarea
                        {...register(`products.${index}.description`)}
                        placeholder="Product description"
                        rows={3}
                        style={{ ...getInputStyle(!!errors.products?.[index]?.description), marginBottom: "0.75rem" }}
                        onFocus={(e) => Object.assign(e.target.style, errors.products?.[index]?.description ? inputErrorStyle : inputFocusStyle)}
                        onBlur={(e) => Object.assign(e.target.style, getInputStyle(!!errors.products?.[index]?.description))}
                        className="placeholder-slate-400"
                        aria-invalid={errors.products?.[index]?.description ? "true" : "false"}
                      />
                      {errors.products?.[index]?.description && (
                        <p style={{ ...errorStyle, marginBottom: "0.75rem" }} role="alert">
                          {errors.products[index]?.description?.message as string}
                        </p>
                      )}
                      <Controller
                        name={`products.${index}.image`}
                        control={control}
                        render={({ field }) => (
                          <Dropzone
                            onDrop={(files) => {
                              field.onChange(files[0] || null);
                              trigger(`products.${index}.image`);
                            }}
                            maxFiles={1}
                            maxSize={10 * 1024 * 1024}
                            value={field.value || undefined}
                            description="PNG, JPG, GIF up to 10MB"
                          />
                        )}
                      />
                      {errors.products?.[index]?.image && (
                        <p style={errorStyle} role="alert">
                          {errors.products[index]?.image?.message as string}
                        </p>
                      )}
                    </div>
                  ))}
                  {(formData.products || []).length < 5 && (
                    <button
                      type="button"
                      onClick={addProduct}
                      style={{
                        padding: "0.875rem",
                        width: "100%",
                        border: "1px dashed #d1d5db",
                        borderRadius: "0.5rem",
                        backgroundColor: "#fafafa",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        color: "#71717a",
                        fontWeight: "500",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f4f4f5";
                        e.currentTarget.style.borderColor = "#a1a1aa";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fafafa";
                        e.currentTarget.style.borderColor = "#d1d5db";
                      }}
                    >
                      + Add Product
                    </button>
                  )}
                  {errors.products && typeof errors.products.message === "string" && (
                    <p style={errorStyle} role="alert">
                      {errors.products.message}
                    </p>
                  )}
                </div>

                {/* Operational Hours */}
                <div id="tutorial-hours" style={{ marginBottom: "2rem" }}>
                  <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    marginBottom: "0.75rem",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
                      <label style={getLabelStyle(!!errors.operationalHours)}>
                        Store Operational Hours <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "0.875rem" }}>
                        <input
                          type="checkbox"
                          checked={isExpandedHours}
                          onChange={(e) => setIsExpandedHours(e.target.checked)}
                          style={{ marginRight: "0.5rem", cursor: "pointer" }}
                        />
                        Expand
                      </label>
                    </div>
                  </div>
                  <div className="operational-hours-container" style={{ display: "grid", gap: "0.75rem" }}>
                    {isExpandedHours ? (
                      // Expanded view: Show all days
                      formData.operationalHours.map((hours: any, index: number) => (
                        <div key={index} style={cardStyle}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                              gap: "0.75rem",
                              alignItems: "center",
                            }}
                            className="operational-hours-row"
                          >
                            <span style={{ fontSize: "0.875rem", fontWeight: "500", gridColumn: "1 / -1" }}>{hours.day}</span>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Open</label>
                              <input
                                type="time"
                                value={hours.open}
                                onChange={(e) => updateOperationalHours(index, "open", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={hours.closed}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: hours.closed ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Close</label>
                              <input
                                type="time"
                                value={hours.close}
                                onChange={(e) => updateOperationalHours(index, "close", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={hours.closed}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: hours.closed ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <label style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              fontSize: "0.875rem", 
                              whiteSpace: "nowrap",
                              gridColumn: "1 / -1",
                              justifyContent: "flex-start",
                              marginTop: "0.25rem"
                            }}>
                              <input
                                type="checkbox"
                                checked={hours.closed}
                                onChange={(e) => updateOperationalHours(index, "closed", e.target.checked)}
                                style={{ marginRight: "0.5rem", cursor: "pointer" }}
                              />
                              Closed
                            </label>
                          </div>
                        </div>
                      ))
                    ) : (
                      // Compact view: Show Weekday/Sat/Sun only
                      <>
                        {/* Weekday (Monday-Friday) */}
                        <div style={cardStyle}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                              gap: "0.75rem",
                              alignItems: "center",
                            }}
                            className="operational-hours-row"
                          >
                            <span style={{ fontSize: "0.875rem", fontWeight: "500", gridColumn: "1 / -1" }}>Weekday</span>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Open</label>
                              <input
                                type="time"
                                value={formData.operationalHours[0]?.open || "09:00"}
                                onChange={(e) => updateWeekdayHours("open", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={formData.operationalHours?.slice(0, 5).some((h: any) => h?.closed) || false}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: formData.operationalHours?.slice(0, 5).some((h: any) => h?.closed) ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Close</label>
                              <input
                                type="time"
                                value={formData.operationalHours[0]?.close || "17:00"}
                                onChange={(e) => updateWeekdayHours("close", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={formData.operationalHours?.slice(0, 5).some((h: any) => h?.closed) || false}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: formData.operationalHours?.slice(0, 5).some((h: any) => h?.closed) ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <label style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              fontSize: "0.875rem", 
                              whiteSpace: "nowrap",
                              gridColumn: "1 / -1",
                              justifyContent: "flex-start",
                              marginTop: "0.25rem"
                            }}>
                              <input
                                type="checkbox"
                                checked={
                                  formData.operationalHours?.slice(0, 5).some((h: any) => h?.closed) || false
                                }
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  updateWeekdayHours("closed", checked);
                                }}
                                style={{ marginRight: "0.5rem", cursor: "pointer" }}
                              />
                              Closed
                            </label>
                          </div>
                        </div>
                        {/* Saturday */}
                        <div style={cardStyle}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                              gap: "0.75rem",
                              alignItems: "center",
                            }}
                            className="operational-hours-row"
                          >
                            <span style={{ fontSize: "0.875rem", fontWeight: "500", gridColumn: "1 / -1" }}>Saturday</span>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Open</label>
                              <input
                                type="time"
                                value={formData.operationalHours[5]?.open || "09:00"}
                                onChange={(e) => updateOperationalHours(5, "open", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={formData.operationalHours[5]?.closed || false}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: formData.operationalHours[5]?.closed ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Close</label>
                              <input
                                type="time"
                                value={formData.operationalHours[5]?.close || "17:00"}
                                onChange={(e) => updateOperationalHours(5, "close", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={formData.operationalHours[5]?.closed || false}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: formData.operationalHours[5]?.closed ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <label style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              fontSize: "0.875rem", 
                              whiteSpace: "nowrap",
                              gridColumn: "1 / -1",
                              justifyContent: "flex-start",
                              marginTop: "0.25rem"
                            }}>
                              <input
                                type="checkbox"
                                checked={formData.operationalHours[5]?.closed || false}
                                onChange={(e) => updateOperationalHours(5, "closed", e.target.checked)}
                                style={{ marginRight: "0.5rem", cursor: "pointer" }}
                              />
                              Closed
                            </label>
                          </div>
                        </div>
                        {/* Sunday */}
                        <div style={cardStyle}>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                              gap: "0.75rem",
                              alignItems: "center",
                            }}
                            className="operational-hours-row"
                          >
                            <span style={{ fontSize: "0.875rem", fontWeight: "500", gridColumn: "1 / -1" }}>Sunday</span>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Open</label>
                              <input
                                type="time"
                                value={formData.operationalHours[6]?.open || "09:00"}
                                onChange={(e) => updateOperationalHours(6, "open", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={formData.operationalHours[6]?.closed || false}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: formData.operationalHours[6]?.closed ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                              <label style={{ fontSize: "0.75rem", color: "#71717a" }}>Close</label>
                              <input
                                type="time"
                                value={formData.operationalHours[6]?.close || "17:00"}
                                onChange={(e) => updateOperationalHours(6, "close", e.target.value)}
                                onClick={handleTimeInputClick}
                                onTouchStart={handleTimeInputClick}
                                disabled={formData.operationalHours[6]?.closed || false}
                                style={{
                                  ...inputStyle,
                                  padding: "0.5rem",
                                  fontSize: "0.875rem",
                                  cursor: formData.operationalHours[6]?.closed ? "not-allowed" : "pointer",
                                  WebkitAppearance: "none",
                                  MozAppearance: "textfield",
                                  minHeight: "44px", // Better touch target for mobile
                                }}
                              />
                            </div>
                            <label style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              fontSize: "0.875rem", 
                              whiteSpace: "nowrap",
                              gridColumn: "1 / -1",
                              justifyContent: "flex-start",
                              marginTop: "0.25rem"
                            }}>
                              <input
                                type="checkbox"
                                checked={formData.operationalHours[6]?.closed || false}
                                onChange={(e) => updateOperationalHours(6, "closed", e.target.checked)}
                                style={{ marginRight: "0.5rem", cursor: "pointer" }}
                              />
                              Closed
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {errors.operationalHours && (
                    <p style={errorStyle} role="alert">
                      {errors.operationalHours.message as string}
                    </p>
                  )}
                </div>

                {/* Contract */}
                <div id="tutorial-contract" style={{ marginBottom: "2rem" }}>
                  <label style={getLabelStyle(!!errors.contract)}>
                    Collaboration Contract <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <div
                    style={{
                      ...cardStyle,
                      border: errors.contract ? "2px solid #ef4444" : "1px solid #e4e4e7",
                      padding: "1.5rem",
                      textAlign: "center",
                    }}
                  >
                    <p style={{ fontSize: "0.875rem", color: "#71717a", marginBottom: "1rem", lineHeight: "1.6" }}>
                      You must read and accept the collaboration contract before registering.
                      {formData.contract && (
                        <span style={{ display: "block", marginTop: "0.5rem", color: "#10b981", fontWeight: "600" }}>
                          ✓ Contract accepted
                        </span>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={handleContractClick}
                      style={{
                        padding: "0.75rem 1.5rem",
                        backgroundColor: formData.contract ? "#f4f4f5" : "#09090b",
                        color: formData.contract ? "#09090b" : "white",
                        border: formData.contract ? "1px solid #e4e4e7" : "none",
                        borderRadius: "0.5rem",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        transition: "all 0.2s",
                        boxShadow: formData.contract ? "none" : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                      }}
                      onMouseEnter={(e) => {
                        if (formData.contract) {
                          e.currentTarget.style.backgroundColor = "#e4e4e7";
                        } else {
                          e.currentTarget.style.backgroundColor = "#18181b";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.contract) {
                          e.currentTarget.style.backgroundColor = "#f4f4f5";
                        } else {
                          e.currentTarget.style.backgroundColor = "#09090b";
                        }
                      }}
                    >
                      {formData.contract ? "View Contract Again" : "Read & Accept Collaboration Contract"}
                    </button>
                  </div>
                  {errors.contract && (
                    <p style={errorStyle} role="alert">
                      {errors.contract.message as string}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <div id="tutorial-submit" style={{ textAlign: "center", marginTop: "2.5rem" }}>
                  <button
                    type="submit"
                    disabled={isSubmitting || isTestingFlow}
                    style={{
                      cursor: (isSubmitting || isTestingFlow) ? "not-allowed" : "pointer",
                      padding: "0.75rem 2rem",
                      textAlign: "center",
                      backgroundColor: (isSubmitting || isTestingFlow) ? "#71717a" : "#09090b",
                      color: "white",
                      width: "100%",
                      maxWidth: "420px",
                      height: "3rem",
                      fontSize: "1rem",
                      fontWeight: "600",
                      borderRadius: "0.5rem",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting && !isTestingFlow) {
                        e.currentTarget.style.backgroundColor = "#18181b";
                        e.currentTarget.style.transform = "translateY(-1px)";
                        e.currentTarget.style.boxShadow = "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = (isSubmitting || isTestingFlow) ? "#71717a" : "#09090b";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)";
                    }}
                  >
                    {(isSubmitting || isTestingFlow) ? "Creating Account..." : "Create Account"}
                  </button>

                  {/* Test Button - for testing the registration flow without submitting to database */}
                  {/* DISABLED: Hidden from UI but kept in code for development/testing purposes */}
                  <button
                    type="button"
                    onClick={testRegistrationFlow}
                    disabled={isSubmitting || isTestingFlow}
                    style={{
                      display: "none", // Hidden from UI
                      cursor: (isSubmitting || isTestingFlow) ? "not-allowed" : "pointer",
                      padding: "0.625rem 1.5rem",
                      textAlign: "center",
                      backgroundColor: (isSubmitting || isTestingFlow) ? "#e4e4e7" : "#ffffff",
                      color: (isSubmitting || isTestingFlow) ? "#a1a1aa" : "#3b82f6",
                      width: "100%",
                      maxWidth: "420px",
                      height: "2.5rem",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      borderRadius: "0.5rem",
                      border: (isSubmitting || isTestingFlow) ? "1px solid #e4e4e7" : "1px solid #3b82f6",
                      marginTop: "0.75rem",
                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting && !isTestingFlow) {
                        e.currentTarget.style.backgroundColor = "#eff6ff";
                        e.currentTarget.style.borderColor = "#2563eb";
                        e.currentTarget.style.color = "#2563eb";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = (isSubmitting || isTestingFlow) ? "#e4e4e7" : "#ffffff";
                      e.currentTarget.style.borderColor = (isSubmitting || isTestingFlow) ? "#e4e4e7" : "#3b82f6";
                      e.currentTarget.style.color = (isSubmitting || isTestingFlow) ? "#a1a1aa" : "#3b82f6";
                    }}
                  >
                    {isTestingFlow ? "Testing Flow..." : "Test Registration Flow (No Database Submit)"}
                  </button>

                  {/* Login link */}
                  <p style={{ paddingTop: "1.5rem", fontSize: "0.875rem", color: "#71717a" }}>
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      style={{
                        color: "#09090b",
                        fontWeight: "500",
                        textDecoration: "none",
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        (e.target as HTMLElement).style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        (e.target as HTMLElement).style.textDecoration = "none";
                      }}
                    >
                      Login here
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Joyride Tutorial */}
      <Joyride
        steps={tutorialSteps}
        run={runTutorial}
        continuous
        showProgress
        showSkipButton
        callback={handleTutorialCallback}
        scrollToFirstStep
        disableOverlayClose
        spotlightClicks={false}
        styles={{
          options: {
            primaryColor: "#5289FF",
            textColor: "#09090b",
            backgroundColor: "#ffffff",
            overlayColor: "rgba(0, 0, 0, 0.5)",
            arrowColor: "#ffffff",
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: "0.75rem",
            padding: "1.5rem",
            fontSize: "0.9375rem",
          },
          tooltipTitle: {
            fontSize: "1.125rem",
            fontWeight: "600",
            marginBottom: "0.75rem",
          },
          tooltipContent: {
            lineHeight: "1.6",
            padding: "0.5rem 0",
          },
          buttonNext: {
            backgroundColor: "#5289FF",
            borderRadius: "0.5rem",
            padding: "0.625rem 1.25rem",
            fontSize: "0.875rem",
            fontWeight: "600",
          },
          buttonBack: {
            color: "#71717a",
            marginRight: "0.75rem",
            fontSize: "0.875rem",
          },
          buttonSkip: {
            color: "#71717a",
            fontSize: "0.875rem",
          },
        }}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish",
          next: "Next",
          skip: "Skip Tour",
        }}
      />

      {/* Contract Modal */}
      {showContract && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
            padding: "1.5rem",
          }}
          onClick={() => setShowContract(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "0.75rem",
              padding: "2.5rem",
              width: "95vw",
              maxWidth: "1400px",
              height: "92vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: "1.5rem", fontSize: "1.75rem", fontWeight: "700", color: "#09090b", textAlign: "center" }}>
              WeCarryBags Collaboration Contract
            </h2>

            {/* PDF Preview Container */}
            <div style={{
              flex: 1,
              marginBottom: "1.5rem",
              border: "1px solid #e4e4e7",
              borderRadius: "0.5rem",
              overflow: "hidden",
              backgroundColor: "#fafafa",
            }}>
              {(() => {
                // Safely parse address
                const address2 = formData.address?.address2 || "";
                let address = address2;
                if (address2 && typeof address2 === 'string' && address2.includes(',')) {
                  const addressParts = address2.split(",").map(part => part.trim());
                  address = addressParts.length >= 2
                    ? `${addressParts[0]}, ${addressParts[1]}`
                    : addressParts[0] || address2;
                }

                const contract = (
                  <DisplayContract
                    retailerName={formData.title || ""}
                    retailerLegalName={formData.legalTitle || ""}
                    address={address}
                    city={formData.address?.city || ""}
                    country={formData.address?.country || ""}
                    postal_code={formData.address?.postal_code || ""}
                    representativeName={formData.representativeName || ""}
                  />
                );

                return (
                  <PDFDownloadLink document={contract} fileName="wecarrybags_collaboration_contract.pdf">
                    {({ blob, url, loading, error }) => {
                      if (url) {
                        return (
                          <iframe
                            src={url}
                            width="100%"
                            height="100%"
                            style={{ border: "none", display: "block" }}
                            title="Collaboration Contract"
                          />
                        );
                      }
                      if (error) {
                        return (
                          <div style={{
                            padding: "2rem",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}>
                            <p style={{ color: "#ef4444", marginBottom: "0.5rem", fontWeight: "600" }}>
                              Unable to load contract preview
                            </p>
                            <p style={{ color: "#71717a", fontSize: "0.875rem" }}>
                              This may be due to browser compatibility. You will receive the full contract via email after registration.
                            </p>
                          </div>
                        );
                      }
                      if (loading) {
                        return (
                          <div style={{
                            padding: "2rem",
                            textAlign: "center",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}>
                            <div
                              style={{
                                width: "3rem",
                                height: "3rem",
                                border: "3px solid #e4e4e7",
                                borderTop: "3px solid #09090b",
                                borderRadius: "50%",
                                animation: "spin 0.8s linear infinite",
                                marginBottom: "1rem",
                              }}
                            />
                            <p style={{ color: "#71717a", fontSize: "0.875rem" }}>
                              Loading contract preview...
                            </p>
                          </div>
                        );
                      }
                      return (
                        <div style={{
                          padding: "2rem",
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}>
                          <p style={{ color: "#71717a" }}>Preparing contract...</p>
                        </div>
                      );
                    }}
                  </PDFDownloadLink>
                );
              })()}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              marginTop: "1.5rem",
              paddingTop: "1.5rem",
              borderTop: "1px solid #e4e4e7",
            }}>
              <button
                onClick={() => setShowContract(false)}
                style={{
                  padding: "0.875rem 2rem",
                  backgroundColor: "#f4f4f5",
                  color: "#09090b",
                  border: "1px solid #e4e4e7",
                  borderRadius: "0.5rem",
                  cursor: "pointer",
                  fontSize: "0.9375rem",
                  fontWeight: "500",
                  transition: "all 0.2s",
                  minWidth: "140px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#e4e4e7";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f4f4f5";
                }}
              >
                {formData.contract ? "Close" : "Cancel"}
              </button>
              <button
                onClick={() => {
                  if (!formData.contract) {
                    setValue("contract", true);
                    trigger("contract");
                    setShowContract(false);
                  }
                }}
                disabled={formData.contract}
                style={{
                  padding: "0.875rem 2.5rem",
                  backgroundColor: formData.contract ? "#d1fae5" : "#10b981",
                  color: formData.contract ? "#065f46" : "white",
                  border: formData.contract ? "1px solid #86efac" : "none",
                  borderRadius: "0.5rem",
                  cursor: formData.contract ? "default" : "pointer",
                  fontSize: "0.9375rem",
                  fontWeight: "600",
                  transition: "all 0.2s",
                  boxShadow: formData.contract ? "none" : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  minWidth: "180px",
                  opacity: formData.contract ? 1 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!formData.contract) {
                    e.currentTarget.style.backgroundColor = "#059669";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 8px -1px rgba(0, 0, 0, 0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!formData.contract) {
                    e.currentTarget.style.backgroundColor = "#10b981";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
                  }
                }}
              >
                {formData.contract ? "✓ Accepted" : "I Accept"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
